import { bouncedTables, elevatorQuote, hubItems, maxBet, minBet, type GameSnapshot } from '../engine/casino';
import { drinkDef } from '../engine/bar';
import { floorDef, FLOORS, nextFloor } from '../engine/floors';
import { GAMES } from '../engine/games';
import { money, withCommas } from '../format';
import type { GameActions } from '../useGame';
import { BuzzMeter } from './parts';

export function Hub({ snapshot, actions }: { snapshot: GameSnapshot; actions: GameActions }) {
  const run = snapshot.run!;
  const floor = floorDef(run.floor);
  const up = nextFloor(run.floor);
  // The ★ table leads (full width on a phone); the engine's order otherwise.
  const items = hubItems(run).map((item, index) => ({ item, index }));
  items.sort((a, b) => Number(b.item.kind === 'game' && b.item.id === run.special) - Number(a.item.kind === 'game' && a.item.id === run.special));
  const isNew = (id: string) => run.floor > 1 && floor.newGames.includes(id as never);
  const bounced = bouncedTables(run);

  return (
    <div className="app">
      <header className="header">
        <div className="block title-block">
          <div>
            <h1>Feelin' Lucky</h1>
            <div className="floor-name">The {floor.name}</div>
          </div>
          <span className="label floor-tag">Floor {run.floor} of {FLOORS.length}</span>
        </div>
        <BuzzMeter bar={run.bar} />
        <div className="block bankroll">
          <div className="label">Bankroll</div>
          <div className="amount">{money(run.bankroll)}</div>
          {/* The floor's progressive jackpot: every bet here adds to it. */}
          <div className="jackpot-pot"><span className="label">Jackpot</span> {money(run.jackpot)}</div>
        </div>
      </header>
      {snapshot.notice && <div className="notice" role="status">{withCommas(snapshot.notice)}</div>}
      <main className="floor">
        {items.map(({ item, index }) => {
          const open = () => actions.openHubItem(index);
          if (item.kind === 'game') {
            const game = GAMES[item.id];
            const star = run.special === item.id;
            const out = bounced.includes(item.id);
            const limits = game.fixedBet ? `Ticket ${money(minBet(item.id, run.floor))}` : `Bets ${money(minBet(item.id, run.floor))}–${money(maxBet(item.id, run.floor))}`;
            return (
              <button key={item.id} className={`ticket${star ? ' star' : ''}${isNew(item.id) ? ' new' : ''}${out ? ' bounced' : ''}`} onClick={open}>
                <span className="name">
                  {game.name}
                  {isNew(item.id) && <span className="new-tag">New</span>}
                  {out && <span className="new-tag bouncer-tag">Drunk</span>}
                </span>
                {/* The description always shows; new games also get a tag (user, 2026-09-27). */}
                <span className="sub">{star ? `House Special: ${game.blurb}` : game.blurb}</span>
                {star && <span className="sub perk">★ {game.specialPerk}</span>}
                {out && <span className="sub perk bouncer">The bouncer won’t seat you while Drunk.</span>}
                <span className="sub meta">{limits} · Jackpot: {game.jackpotRule}</span>
                {star && <span className="starburst" aria-label="House Special" />}
              </button>
            );
          }
          if (item.kind === 'elevator' && up) {
            return (
              <button key="elevator" className="ticket elevator" onClick={open}>
                <span className="label" style={{ padding: '10px 14px 0' }}>Elevator ▲</span>
                <span className="name">The {up.name}</span>
                <span className="sub">Fare {money(elevatorQuote(run)?.cost ?? up.elevatorCost)}</span>
              </button>
            );
          }
          const bar = item.kind === 'bar';
          return (
            <button key={item.kind} className="ticket service" onClick={open}>
              <span className="name">{bar ? 'The Bar' : 'Gift Shop'}</span>
              <span className="sub">{bar ? (run.bar.drinks.length ? run.bar.drinks.map((d) => `${drinkDef(d.id).name}${d.playsLeft === null ? '' : ` · ${d.playsLeft} left`}`).join(', ') : 'Drinks for this floor') : 'Upgrades for your next run'}</span>
            </button>
          );
        })}
      </main>
      <button className="link-back" onClick={actions.back}>Leave the casino</button>
    </div>
  );
}
